import { flow, pipe } from 'fp-ts/function';
import { array, ord, string } from 'fp-ts';
import { CalendarService, StructuredDate } from '@optimus-web/core';
import { Amount, PaymentPlanCreateParameters, ReservationLinkResult } from '@mews-commander/data-access';
import { WithAccountId } from '@mews-commander/types';
import { isDefined } from '@mews/std';
import { DateTime, DateTimeService, MonthService } from '@mews/framework';
import { CreatePaymentPlanFormValues } from './schema';
import { MonthlyPaymentPlanAmount, PaymentPlanIntervalKind } from './types';

export const MIN_PAYMENT_PLAN_DAYS = 28;

export const getFormatStructuredDate =
    ({ getFormattedDateString, getDateString }: CalendarService, dateFormat: string) =>
    (structuredDate: StructuredDate) =>
        getFormattedDateString ? getFormattedDateString(structuredDate, dateFormat) : getDateString(structuredDate);

export const preparePaymentPlanCreateParameters = ({
    accountId,
    formValues,
}: {
    formValues: CreatePaymentPlanFormValues;
} & WithAccountId): PaymentPlanCreateParameters => {
    const { planName, paymentMethods, message, note, reservationId, paymentPlanIntervalKind } = formValues;

    const addCustomInterval = paymentPlanIntervalKind === PaymentPlanIntervalKind.Custom && formValues.startDateUtc;

    return {
        name: planName,
        customerId: accountId,
        paymentRequest: { paymentMethods, message, note },
        reservationId,
        ...(addCustomInterval && {
            startDateUtc: formValues.startDateUtc,
        }),
    };
};

const monthIndexOrd = pipe(
    string.Ord,
    ord.contramap(({ monthIndex }: MonthlyPaymentPlanAmount) => monthIndex)
);

export const getSortedMonthlyPaymentPlanAmounts = flow(
    array.filter((amount: MonthlyPaymentPlanAmount | undefined) => isDefined(amount)),
    array.sort(monthIndexOrd)
);

export const isReservationValid = (
    reservation: Pick<ReservationLinkResult, 'reservationStartUtc' | 'reservationEndUtc'>,
    getNowDateTime: () => DateTime,
    timezone: string
) => {
    const reservationStartDateTime = DateTimeService.fromUtcString(reservation.reservationStartUtc, timezone);
    const reservationEndDateTime = DateTimeService.fromUtcString(reservation.reservationEndUtc, timezone);

    if (DateTimeService.diffDays(reservationEndDateTime, reservationStartDateTime) < MIN_PAYMENT_PLAN_DAYS) {
        return false;
    }

    const nowMonth = DateTimeService.toMonth(getNowDateTime());
    const reservationStartMonth = DateTimeService.toMonth(reservationStartDateTime);
    const reservationEndMonth = DateTimeService.toMonth(reservationEndDateTime);

    const possiblePlanStartMonth = MonthService.isLessOrEqual(nowMonth, reservationStartMonth)
        ? MonthService.max(nowMonth, reservationStartMonth)
        : MonthService.max(MonthService.addMonths(nowMonth, 1), reservationStartMonth);

    return MonthService.diffMonths(reservationEndMonth, possiblePlanStartMonth) >= 1;
};

export const getDefaultAmount = (amount: Amount) => amount.source ?? amount;
